/* ==========================================================================
   Theme UI Core — Tabs Controller
   Web Component: <tabs-element>
   ========================================================================== */

if (!customElements.get('tabs-element')) {
    class TabsElement extends HTMLElement {
        constructor() {
            super();
            this.tabs = [];
            this.panels = [];
            this.activeTabId = null;

            this._onKeyDown = this._onKeyDown.bind(this);
            this._onClick = this._onClick.bind(this);
            this._onBlockSelect = this._onBlockSelect.bind(this);
        }

        connectedCallback() {
            // When no tablist is provided (e.g. the Tabs block feeds only panels),
            // build the trigger strip from each panel's [data-tab-trigger] source.
            this._buildTabListIfNeeded();

            this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
            this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

            if (this.tabs.length === 0 || this.panels.length === 0) return;

            this._bindEvents();
            this._setupA11y();

            const defaultPanel = this.panels.find((panel) => panel.dataset.tabDefault === 'true');
            const defaultId = defaultPanel ? defaultPanel.getAttribute('aria-labelledby') : this.dataset.defaultValue;
            const tabToActivate = this.tabs.find((tab) => tab.id === defaultId) || this.tabs[0];

            if (tabToActivate) {
                this.activateTab(tabToActivate.id, false);
            }

            this._setupDesignMode();
        }

        disconnectedCallback() {
            if (this._designModeBound) {
                document.removeEventListener('shopify:block:select', this._onBlockSelect);
            }
        }

        _buildTabListIfNeeded() {
            if (this.querySelector('.tabs__list')) return;

            const panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
            if (panels.length === 0) return;

            const list = document.createElement('div');
            list.className = 'tabs__list';
            list.setAttribute('role', 'tablist');
            list.setAttribute('aria-orientation', this.dataset.orientation === 'vertical' ? 'vertical' : 'horizontal');

            panels.forEach((panel, index) => {
                let tabId = panel.getAttribute('aria-labelledby');
                if (!tabId) {
                    tabId = (panel.id || 'tab-' + index) + '-trigger';
                    panel.setAttribute('aria-labelledby', tabId);
                }

                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'tabs__trigger';
                btn.setAttribute('role', 'tab');
                btn.id = tabId;
                if (panel.id) btn.setAttribute('aria-controls', panel.id);

                const source = panel.querySelector('[data-tab-trigger]');
                if (source) {
                    while (source.firstChild) btn.appendChild(source.firstChild);
                    source.remove();
                } else {
                    btn.textContent = panel.getAttribute('data-tab-label') || 'Tab ' + (index + 1);
                }

                list.appendChild(btn);
            });

            const anchor = this.querySelector('.tabs__content-wrapper') || this.firstChild;
            this.insertBefore(list, anchor);
        }

        _setupDesignMode() {
            if (!window.Shopify || !window.Shopify.designMode) return;
            document.addEventListener('shopify:block:select', this._onBlockSelect);
            this._designModeBound = true;
        }

        _onBlockSelect(e) {
            const panel = e.target.closest('[role="tabpanel"]');
            if (!panel || !this.contains(panel)) return;

            const tabId = panel.getAttribute('aria-labelledby');
            if (tabId) this.activateTab(tabId, false);
        }

        _setupA11y() {
            const tabList = this.querySelector('.tabs__list');
            if (tabList && !tabList.hasAttribute('role')) {
                tabList.setAttribute('role', 'tablist');
            }

            this.tabs.forEach((tab) => {
                tab.setAttribute('tabindex', '-1');
                tab.setAttribute('aria-selected', 'false');
            });

            this.panels.forEach((panel) => {
                panel.setAttribute('tabindex', '0');
                if (!panel.hasAttribute('hidden')) {
                    panel.setAttribute('hidden', 'true');
                }
            });
        }

        _bindEvents() {
            const tabList = this.querySelector('.tabs__list');
            if (tabList) {
                tabList.addEventListener('keydown', this._onKeyDown);
                tabList.addEventListener('click', this._onClick);
            } else {
                this.tabs.forEach((tab) => {
                    tab.addEventListener('keydown', this._onKeyDown);
                    tab.addEventListener('click', this._onClick);
                });
            }
        }

        _onClick(e) {
            const target = e.target.closest('[role="tab"]');
            if (!target || target.disabled || target.getAttribute('aria-disabled') === 'true') return;

            this.activateTab(target.id, true);
        }

        _onKeyDown(e) {
            const target = e.target.closest('[role="tab"]');
            if (!target) return;

            const currentIndex = this.tabs.indexOf(target);
            let nextIndex = currentIndex;

            const orientation = this.dataset.orientation || 'horizontal';

            switch (e.key) {
                case 'ArrowRight':
                    if (orientation === 'horizontal') nextIndex = currentIndex + 1;
                    break;
                case 'ArrowLeft':
                    if (orientation === 'horizontal') nextIndex = currentIndex - 1;
                    break;
                case 'ArrowDown':
                    if (orientation === 'vertical') nextIndex = currentIndex + 1;
                    break;
                case 'ArrowUp':
                    if (orientation === 'vertical') nextIndex = currentIndex - 1;
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'End':
                    nextIndex = this.tabs.length - 1;
                    break;
                default:
                    return;
            }

            e.preventDefault();

            if (nextIndex >= this.tabs.length) {
                nextIndex = 0;
            } else if (nextIndex < 0) {
                nextIndex = this.tabs.length - 1;
            }

            const nextTab = this.tabs[nextIndex];

            if (nextTab.disabled || nextTab.getAttribute('aria-disabled') === 'true') {
                return;
            }

            nextTab.focus();
            this.activateTab(nextTab.id, true);
        }

        activateTab(tabId, focus = false) {
            if (this.activeTabId === tabId) return;

            this.tabs.forEach((tab) => {
                const isSelected = tab.id === tabId;
                tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
                tab.setAttribute('tabindex', isSelected ? '0' : '-1');

                if (isSelected && focus) {
                    tab.focus();
                }
            });

            this.panels.forEach((panel) => {
                const controlledBy = panel.getAttribute('aria-labelledby');
                if (controlledBy === tabId) {
                    panel.removeAttribute('hidden');
                    panel.classList.add('tabs__panel--active');
                } else {
                    panel.setAttribute('hidden', 'true');
                    panel.classList.remove('tabs__panel--active');
                }
            });

            this.activeTabId = tabId;

            this.dispatchEvent(
                new CustomEvent('theme:tabs:change', {
                    bubbles: true,
                    detail: { activeTabId: tabId },
                })
            );
        }
    }

    customElements.define('tabs-element', TabsElement);
}
